import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Receipt, Search, ChevronLeft, ChevronRight, ExternalLink,
  Eye, X, Loader2, FileText
} from 'lucide-react';

const PRIMARY = '#0099FF';
const ITEMS_PER_PAGE = 25;

const formatCurrency = (value) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ClienteFacturacion = ({ embedded = false }) => {
  const { token, API_URL, activeClient } = useAuth();

  const [facturas, setFacturas] = useState([]);
  const [totalFacturas, setTotalFacturas] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);

  const [previewFactura, setPreviewFactura] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);

  const totalPages = Math.ceil(totalFacturas / ITEMS_PER_PAGE);

  const fetchFacturas = useCallback(async (currentPage = 1, search = '') => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });
      if (search.trim()) params.append('search', search.trim());

      const res = await fetch(`${API_URL}/commercial/facturas?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar facturas');
      const data = await res.json();
      setFacturas(data.facturas || []);
      setTotalFacturas(data.pagination?.total || data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchFacturas(page, searchTerm);
    // Re-cargar cuando cambia el cliente activo (selector lateral).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeClient?.id]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setPage(1);
      fetchFacturas(1, searchTerm);
    }, 400);
    setSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Baja el PDF de la factura del backend (con auth) como blob. El backend lo
  // sirve por streaming desde el FTP: asi el PDF es el real y no la landing de la
  // SPA (antes se apuntaba a comprassanitarias.com/facturas/*, que la SPA no sirve).
  const fetchFacturaBlobUrl = async (factura) => {
    const res = await fetch(`${API_URL}/commercial/facturas/${encodeURIComponent(factura.nombre_archivo)}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('No se pudo abrir la factura');
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  };

  const openFacturaPdf = async (factura) => {
    try {
      const url = await fetchFacturaBlobUrl(factura);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setError(e.message);
    }
  };

  // Al abrir la vista previa, bajamos el PDF y lo mostramos en el iframe via blob.
  useEffect(() => {
    if (!previewFactura) return undefined;
    let objUrl = null;
    let cancelled = false;
    setPreviewPdfUrl(null);
    fetchFacturaBlobUrl(previewFactura)
      .then((url) => { if (cancelled) URL.revokeObjectURL(url); else { objUrl = url; setPreviewPdfUrl(url); } })
      .catch(() => { if (!cancelled) setError('No se pudo cargar la factura'); });
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); setPreviewPdfUrl(null); };
  }, [previewFactura]); // eslint-disable-line react-hooks/exhaustive-deps

  const getInvoiceNumber = (factura) => {
    if (!factura.nombre_archivo) return `#${factura.id}`;
    return factura.nombre_archivo.replace(/\.pdf$/i, '');
  };

  return (
    <div className={embedded ? 'p-6' : 'h-full overflow-y-auto p-6'}>

      {/* Header — solo visible cuando NO está embebido en pestañas */}
      {!embedded && (
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
            <Receipt className="w-5 h-5" style={{ color: PRIMARY }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Mis Facturas</h1>
            <p className="text-sm text-gray-500">{totalFacturas} facturas encontradas</p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por N° de factura..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N° Factura</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Base Imponible</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    <p className="text-sm text-gray-500 mt-2">Cargando facturas...</p>
                  </td>
                </tr>
              ) : facturas.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No se encontraron facturas</p>
                  </td>
                </tr>
              ) : (
                facturas.map((factura) => (
                  <tr key={factura.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{getInvoiceNumber(factura)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500">{formatDate(factura.fecha)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm text-gray-600">{formatCurrency(factura.base)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(factura.final)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setPreviewFactura(factura)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Vista previa"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => openFacturaPdf(factura)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Ver PDF"
                        >
                          <ExternalLink size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500">
              Página {page} de {totalPages} · {totalFacturas} facturas
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewFactura && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPreviewFactura(null)}>
          <div className="bg-white rounded-xl shadow-xl w-[90vw] max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <div>
                <p className="text-sm font-semibold text-gray-800">Factura: {getInvoiceNumber(previewFactura)}</p>
                <p className="text-xs text-gray-500">Fecha: {formatDate(previewFactura.fecha)} · Total: {formatCurrency(previewFactura.final)}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openFacturaPdf(previewFactura)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-white hover:opacity-90 transition-colors"
                  style={{ backgroundColor: PRIMARY }}
                >
                  <ExternalLink size={12} />
                  Abrir PDF
                </button>
                <button
                  onClick={() => setPreviewFactura(null)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            {/* PDF iframe */}
            <div className="flex-1 overflow-hidden">
              {previewPdfUrl ? (
                <iframe
                  src={previewPdfUrl}
                  className="w-full h-full border-0"
                  title={`Factura ${getInvoiceNumber(previewFactura)}`}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-sm text-gray-400">
                  Cargando factura…
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClienteFacturacion;
